import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useServices } from '@/shared/services'
import type { AuthProvider, Session } from './auth-provider'
import { can, type Permission } from './permissions'

export const sessionQueryKey = ['auth', 'session'] as const

export function sessionQueryOptions(auth: AuthProvider) {
  return queryOptions({
    queryKey: sessionQueryKey,
    queryFn: () => auth.getSession(),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** Returns the cached session when fresh, otherwise asks the AuthProvider. For route guards. */
export function fetchSession(
  queryClient: QueryClient,
  auth: AuthProvider,
): Promise<Session | null> {
  return queryClient.query(sessionQueryOptions(auth))
}

export function useSession(): Session | null {
  const { auth } = useServices()
  return useQuery(sessionQueryOptions(auth)).data ?? null
}

export function useCan(permission: Permission): boolean {
  return can(useSession(), permission)
}

export function clearSession(queryClient: QueryClient): void {
  queryClient.setQueryData<Session | null>(sessionQueryKey, null)
}

export function useSessionActions() {
  const { auth } = useServices()
  const queryClient = useQueryClient()
  return {
    login: async (...args: Parameters<AuthProvider['login']>) => {
      const session = await auth.login(...args)
      queryClient.setQueryData(sessionQueryKey, session)
      return session
    },
    logout: async () => {
      try {
        await auth.logout()
      } finally {
        // Drop every cached server response of the previous user.
        queryClient.clear()
        clearSession(queryClient)
      }
    },
  }
}
